import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { get, post, put } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen, Modal } from "../../../components/admin/EssUi";
import { BarChart3, Search } from "lucide-react";

const AdminPanelExpensesPage = () => {
  const { permissions } = useSelector((state) => state.auth) || {};
  const canManage = !!permissions?.canManageExpenses;
  const [searchParams] = useSearchParams();
  const saleOrderFilter = searchParams.get("sale_order_id") || "";
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  
  useEffect(() => { setPage(1); }, [search, catFilter, deptFilter, statusFilter, activeTab]);

  const [viewRow, setViewRow] = useState(null);
  const [showReports, setShowReports] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({ 
    employee_id: "", 
    category_id: "", 
    name: "", 
    amount: "", 
    date: new Date().toISOString().split("T")[0] 
  });
  const [empOptions, setEmpOptions] = useState([]);

  // Fonction pour formater les données
  const formatExpenseData = (expenses, employees) => {
    const empMap = {};
    employees.forEach((e) => { empMap[e.id] = e; });
    
    return expenses.map((e, i) => {
      const emp = empMap[e.employee_id] || {};
      const dt = e.date ? new Date(e.date) : null;
      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      
      // Déterminer le statut correct
      let status = "Submitted";
      if (e.state === "draft") status = "Pending";
      else if (e.state === "submitted") status = "Under Review";
      else if (e.state === "approved") status = "Approved";
      else if (e.state === "paid") status = "Paid";
      else if (e.state === "refused") status = "Refused";
      
      return {
        id: e.id || i,
        ref: `EXP-2026-${String(e.id || i + 1).padStart(3, "0")}`,
        employee: e.employee_name || emp.name || "—",
        employeeId: e.employee_id,
        department: emp.department || emp.department_id || "—",
        category: e.category || e.category_id || "General",
        description: e.name || e.description || "—",
        amountRaw: parseFloat(e.amount) || 0,
        amount: `${(parseFloat(e.amount) || 0).toLocaleString()} FCFA`,
        receipts: e.receipts ? `${e.receipts} receipt${e.receipts > 1 ? 's' : ''}` : "1 receipt",
        submitted: dt ? dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : "—",
        submittedFull: e.date || "—",
        monthShort: dt ? monthNames[dt.getMonth()] : "—",
        state: e.state || "submitted",
        status: status
      };
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const [expRes, empRes] = await Promise.all([
          get("/api/expenses"),
          get("/api/employees?limit=500"),
        ]);
        
        const expRows = expRes?.data || [];
        const empRows = empRes?.data || [];
        
        setEmpOptions(empRows || []);
        setData(formatExpenseData(expRows, empRows));
      } catch (error) {
        console.error("Error loading data:", error);
        setData([]);
      }
      setLoaded(true);
    })();
  }, []);

  const refreshData = async () => {
    try {
      const [expRes, empRes] = await Promise.all([
        get("/api/expenses"),
        get("/api/employees?limit=500"),
      ]);
      
      const expRows = expRes?.data || [];
      const empRows = empRes?.data || [];
      
      setEmpOptions(empRows || []);
      setData(formatExpenseData(expRows, empRows));
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const handleAction = async (id, newState) => {
    try {
      const comment = newState === "approved" ? "Approved by manager" : 
                     newState === "refused" ? "Refused by manager" : undefined;
      await put(`/api/expenses/${id}`, { 
        state: newState, 
        ess_manager_comment: comment 
      });
      setViewRow(null);
      await refreshData();
    } catch (e) { 
      alert("Action failed: " + e.message); 
    }
  };

  const handleCreate = async () => {
    if (!formData.employee_id || !formData.amount) { 
      alert("Employee and amount are required"); 
      return; 
    }
    setFormLoading(true);
    try {
      const vals = {
        employee_id: parseInt(formData.employee_id),
        category_id: formData.category_id || 1,
        name: formData.name || "Expense",
        amount: parseFloat(formData.amount),
        date: formData.date || new Date().toISOString().split("T")[0],
      };
      await post("/api/expenses", vals);
      setShowNewForm(false);
      setFormData({ 
        employee_id: "", 
        category_id: "", 
        name: "", 
        amount: "", 
        date: new Date().toISOString().split("T")[0] 
      });
      await refreshData();
    } catch (e) { 
      alert("Create failed: " + e.message); 
    }
    setFormLoading(false);
  };

  if (!loaded) return <LoadingScreen />;

  // Extraction des catégories et départements uniques
  const cats = [...new Set(data.map(r => r.category).filter(Boolean))];
  const depts = [...new Set(data.map(r => r.department).filter(d => d && d !== "—"))];

  // Filtrage des données — ?sale_order_id n'existe pas côté API expenses (pas de colonne sale_order_id), on ne filtre pas
  const filtered = data.filter(r => {
    if (activeTab !== "all" && r.status !== activeTab) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (catFilter !== "all" && r.category !== catFilter) return false;
    if (deptFilter !== "all" && r.department !== deptFilter) return false;
    if (search.trim() && !r.employee.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Onglets
  const tabs = [
    { key: "all", label: "All", count: data.length },
    { key: "Submitted", label: "Submitted", count: data.filter(r => r.status === "Submitted").length },
    { key: "Under Review", label: "Under Review", count: data.filter(r => r.status === "Under Review").length },
    { key: "Approved", label: "Approved", count: data.filter(r => r.status === "Approved").length },
    { key: "Refused", label: "Refused", count: data.filter(r => r.status === "Refused").length },
    { key: "Paid", label: "Paid", count: data.filter(r => r.status === "Paid").length },
  ];

  // Styles et couleurs
  const getStatusColor = (status) => {
    const colors = {
      "Submitted": "#4f46e5", 
      "Under Review": "#d97706", 
      "Approved": "#16a34a",
      "Refused": "#e11d48", 
      "Paid": "#0891b2", 
      "Pending": "#94a3b8"
    };
    return colors[status] || "#64748b";
  };

  const getStatusBg = (status) => {
    const colors = {
      "Submitted": "#eef2ff", 
      "Under Review": "#fef3c7", 
      "Approved": "#dcfce7",
      "Refused": "#fecdd3", 
      "Paid": "#cffafe", 
      "Pending": "#f1f5f9"
    };
    return colors[status] || "#f1f5f9";
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Travel": "#4f46e5", 
      "Accommodation": "#16a34a", 
      "Meals": "#d97706",
      "Equipment": "#7c3aed", 
      "Training": "#0891b2", 
      "Other": "#db2777",
      "Office Supplies": "#ea580c", 
      "General": "#64748b"
    };
    return colors[category] || "#64748b";
  };

  const getCategoryBg = (category) => {
    const colors = {
      "Travel": "#eef2ff", 
      "Accommodation": "#dcfce7", 
      "Meals": "#fef3c7",
      "Equipment": "#f3e8ff", 
      "Training": "#cffafe", 
      "Other": "#fce7f3",
      "Office Supplies": "#ffedd5", 
      "General": "#f1f5f9"
    };
    return colors[category] || "#f1f5f9";
  };

  // Calculs pour les graphiques
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const byMonth = {};
  const byCat = {};
  
  data.forEach(r => {
    const m = r.monthShort || "Jun";
    const amt = r.amountRaw || 0;
    byMonth[m] = (byMonth[m] || 0) + amt;
    byCat[r.category] = (byCat[r.category] || 0) + amt;
  });
  
  const monthlyTrend = monthNames
    .filter(m => byMonth[m])
    .map(m => ({ month: m, amount: Math.round(byMonth[m]) }));
  
  const catTotal = Object.values(byCat).reduce((s, v) => s + v, 0);
  const catColors = ["#4f46e5","#16a34a","#d97706","#7c3aed","#0891b2","#db2777","#ea580c","#65a30d","#9333ea"];
  const categoryBreakdown = Object.entries(byCat)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, amt], i) => ({ 
      category: cat, 
      amount: catTotal > 0 ? Math.round((amt / catTotal) * 100) : 0, 
      color: catColors[i % catColors.length] 
    }));
  
  const maxAmount = monthlyTrend.length > 0 ? Math.max(...monthlyTrend.map(d => d.amount)) : 1;

  // Donut chart slices
  let angleAcc = 0;
  const donutSlices = categoryBreakdown.map(item => {
    const pct = item.amount;
    const start = (angleAcc / 100) * 2 * Math.PI;
    angleAcc += pct;
    const end = (angleAcc / 100) * 2 * Math.PI;
    const x1 = 60 + 50 * Math.cos(start);
    const y1 = 60 + 50 * Math.sin(start);
    const x2 = 60 + 50 * Math.cos(end);
    const y2 = 60 + 50 * Math.sin(end);
    const largeArc = pct > 50 ? 1 : 0;
    return { ...item, d: `M 60 60 L ${x1} ${y1} A 50 50 0 ${largeArc} 1 ${x2} ${y2} Z` };
  });

  // Styles communs
  const inputStyle = {
    width: '100%', 
    padding: '10px 12px', 
    border: '1px solid #e2e8f0', 
    borderRadius: '8px',
    fontSize: '14px', 
    outline: 'none', 
    background: '#f8fafc', 
    boxSizing: 'border-box'
  };
  
  const labelStyle = { 
    fontSize: '13px', 
    fontWeight: '600', 
    color: '#334155', 
    marginBottom: '6px', 
    display: 'block' 
  };

  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  // Calcul des statistiques
  const pendingCount = data.filter(r => r.status === "Submitted" || r.status === "Under Review" || r.status === "Pending").length;
  const pendingAmt = data.filter(r => r.status === "Submitted" || r.status === "Under Review" || r.status === "Pending")
    .reduce((s, r) => s + (r.amountRaw || 0), 0);
  const approvedCount = data.filter(r => r.status === "Approved" || r.status === "Paid").length;
  const approvedAmt = data.filter(r => r.status === "Approved" || r.status === "Paid")
    .reduce((s, r) => s + (r.amountRaw || 0), 0);
  const totalSpend = data.reduce((s, r) => s + (r.amountRaw || 0), 0);
  const avgAmt = data.length > 0 ? totalSpend / data.length : 0;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      {saleOrderFilter && (
        <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Smart Button Expenses depuis SO <strong>#{saleOrderFilter}</strong> — pas de lien direct sale_order→expenses côté API, affichage de toutes les dépenses</span>
          <a href="/admin-panel/expenses" style={{ color: "#92400e", textDecoration: "underline" }}>Voir tout</a>
        </div>
      )}
      <PageHeader
        title="Expense Claims"
        subtitle={saleOrderFilter ? `Expenses linked to SO #${saleOrderFilter}` : "Review and approve employee expense submissions"}
        actions={
          <div style={{ display: 'flex', gap: '12px' }}>
            {canManage && (
              <button 
                onClick={() => setShowNewForm(true)} 
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm" 
                style={{ background: "#059669", color: "white", border: 'none', cursor: 'pointer' }}
              >
                ＋ New Expense
              </button>
            )}
            <button 
              onClick={() => setShowReports(true)} 
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all" 
              style={{ background: "#f1f5f9", color: "#334155", border: 'none', cursor: 'pointer' }}
            >
              <BarChart3 size={16} />
              Reports
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="ess-sg ess-sg4" style={{ marginBottom: '24px' }}>
        <StatCard 
          label="Pending Review" 
          value={String(pendingCount)} 
          icon="⏳" 
          color="amber" 
          trend={pendingAmt.toLocaleString() + " FCFA total"} 
        />
        <StatCard 
          label="Approved" 
          value={String(approvedCount)} 
          icon="✅" 
          color="green" 
          trend={approvedAmt.toLocaleString() + " FCFA total"} 
        />
        <StatCard 
          label="Total Spend" 
          value={totalSpend.toLocaleString() + " FCFA"} 
          icon="💰" 
          color="indigo" 
          trend={`${data.length} claims total`} 
        />
        <StatCard 
          label="Avg Claim Amount" 
          value={Math.round(avgAmt).toLocaleString() + " FCFA"} 
          icon="📊" 
          color="slate" 
          trend="Per submission" 
        />
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '4px', 
        marginBottom: '20px', 
        borderBottom: '1px solid #e2e8f0', 
        paddingBottom: '4px', 
        overflowX: 'auto' 
      }}>
        {tabs.map((tab) => (
          <button 
            key={tab.key} 
            onClick={() => setActiveTab(tab.key)} 
            style={{ 
              padding: '8px 20px', 
              border: 'none', 
              background: activeTab === tab.key ? '#059669' : 'transparent', 
              color: activeTab === tab.key ? 'white' : '#475569', 
              borderRadius: '8px', 
              fontSize: '13px', 
              fontWeight: activeTab === tab.key ? '600' : '500', 
              cursor: 'pointer', 
              transition: 'all 0.2s', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              whiteSpace: 'nowrap' 
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ 
                background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#f1f5f9', 
                padding: '2px 8px', 
                borderRadius: '12px', 
                fontSize: '11px', 
                fontWeight: '600' 
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        flexWrap: 'wrap', 
        marginBottom: '24px', 
        background: 'white', 
        padding: '16px', 
        borderRadius: '12px', 
        border: '1px solid #e2e8f0' 
      }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
          <input 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            placeholder="Search employee, expense..." 
            style={{ 
              width: '100%', 
              padding: '10px 12px 10px 36px', 
              border: '1px solid #e2e8f0', 
              borderRadius: '8px', 
              fontSize: '14px', 
              outline: 'none', 
              transition: 'all 0.2s', 
              background: '#f8fafc', 
              boxSizing: 'border-box' 
            }} 
            onFocus={(e) => e.target.style.borderColor = '#4f46e5'} 
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} 
          />
        </div>
        <select 
          value={catFilter} 
          onChange={(e) => setCatFilter(e.target.value)} 
          style={{ 
            padding: '10px 12px', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            background: '#f8fafc', 
            outline: 'none', 
            cursor: 'pointer', 
            minWidth: '160px' 
          }}
        >
          <option value="all">All Categories</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select 
          value={deptFilter} 
          onChange={(e) => setDeptFilter(e.target.value)} 
          style={{ 
            padding: '10px 12px', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            background: '#f8fafc', 
            outline: 'none', 
            cursor: 'pointer', 
            minWidth: '160px' 
          }}
        >
          <option value="all">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)} 
          style={{ 
            padding: '10px 12px', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px', 
            fontSize: '14px', 
            background: '#f8fafc', 
            outline: 'none', 
            cursor: 'pointer', 
            minWidth: '160px' 
          }}
        >
          <option value="all">All Status</option>
          <option value="Submitted">Submitted</option>
          <option value="Under Review">Under Review</option>
          <option value="Approved">Approved</option>
          <option value="Refused">Refused</option>
          <option value="Paid">Paid</option>
        </select>
      </div>

      {/* Main Content */}
      <div style={{ display: 'grid', gap: '24px' }} className="ess-rg-2-1">
        {/* Table */}
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '10%' }}>Expense #</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '12%' }}>Employee</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '10%' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '15%' }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '12%' }}>Amount</th>
                  <th style={{ textAlign: 'center', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '8%' }}>Receipts</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '10%' }}>Submitted</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '12%' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', width: '7%' }}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px 16px', color: '#94a3b8', fontSize: '14px' }}>
                      No expenses found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <tr key={row.id} style={{ borderBottom: index < paginatedData.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '500', color: '#4f46e5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.ref}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '500', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.employee}</td>
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{ 
                          background: getCategoryBg(row.category), 
                          color: getCategoryColor(row.category), 
                          padding: '4px 12px', 
                          borderRadius: '12px', 
                          fontSize: '12px', 
                          fontWeight: '500', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {row.category}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.description}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b', textAlign: 'right', whiteSpace: 'nowrap' }}>{row.amount}</td>
                      <td style={{ padding: '14px 16px', fontSize: '12px', color: '#64748b', textAlign: 'center', whiteSpace: 'nowrap' }}>{row.receipts}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>{row.submitted}</td>
                      <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                        <span style={{ 
                          background: getStatusBg(row.status), 
                          color: getStatusColor(row.status), 
                          padding: '4px 12px', 
                          borderRadius: '12px', 
                          fontSize: '12px', 
                          fontWeight: '600', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button 
                          onClick={() => setViewRow(row)} 
                          style={{ 
                            border: 'none', 
                            background: 'transparent', 
                            color: '#4f46e5', 
                            fontSize: '13px', 
                            fontWeight: '500', 
                            cursor: 'pointer', 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            transition: 'all 0.2s' 
                          }} 
                          onMouseEnter={(e) => e.target.style.background = '#eef2ff'} 
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '12px 16px', 
            borderTop: '1px solid #e2e8f0', 
            background: '#fafafa' 
          }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              Showing {filtered.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button 
                disabled={page <= 1} 
                onClick={() => setPage(p => Math.max(1, p - 1))} 
                style={{ 
                  padding: '6px 12px', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '6px', 
                  background: page <= 1 ? '#f1f5f9' : 'white', 
                  color: page <= 1 ? '#94a3b8' : '#059669', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  cursor: page <= 1 ? 'not-allowed' : 'pointer' 
                }}
              >
                ← Prev
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button 
                    key={p} 
                    onClick={() => setPage(p)} 
                    style={{ 
                      padding: '6px 12px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '6px', 
                      background: p === page ? '#059669' : 'white', 
                      color: p === page ? 'white' : '#334155', 
                      fontSize: '13px', 
                      fontWeight: p === page ? '600' : '500', 
                      cursor: 'pointer' 
                    }}
                  >
                    {p}
                  </button>
                );
              })}
              <button 
                disabled={page >= totalPages} 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                style={{ 
                  padding: '6px 12px', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '6px', 
                  background: page >= totalPages ? '#f1f5f9' : 'white', 
                  color: page >= totalPages ? '#94a3b8' : '#059669', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer' 
                }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', minWidth: 0 }}>
          {/* Monthly Expense Trend */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '20px', paddingTop: '4px' }}>Monthly Expense Trend</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', height: '140px', gap: '8px' }}>
              {monthlyTrend.length === 0 ? (
                <div style={{ width: '100%', textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '13px' }}>No data</div>
              ) : monthlyTrend.map((item) => {
                const height = (item.amount / maxAmount) * 100;
                return (
                  <div key={item.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      width: '100%', 
                      height: `${Math.max(height, 4)}px`, 
                      background: item.month === 'Jun' ? '#4f46e5' : '#818cf8', 
                      borderRadius: '4px 4px 0 0', 
                      minHeight: '4px', 
                      transition: 'height 0.5s ease', 
                      opacity: item.month === 'Jun' ? 1 : 0.6 
                    }} />
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '8px', fontWeight: '500' }}>{item.month}</div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#1e293b' }}>{(item.amount/1000).toFixed(0)}k</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Spend by Category */}
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Spend by Category</h3>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
              <div style={{ position: 'relative', width: '120px', height: '120px', flexShrink: 0 }}>
                <svg viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                  {donutSlices.length === 0 ? (
                    <circle cx="60" cy="60" r="50" fill="#f1f5f9" />
                  ) : donutSlices.map((item) => (
                    <path key={item.category} d={item.d} fill={item.color} opacity={0.9} />
                  ))}
                  <circle cx="60" cy="60" r="30" fill="white" />
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>100%</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>Total</div>
                </div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
                {categoryBreakdown.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No data</div>
                ) : categoryBreakdown.map((item) => (
                  <div key={item.category} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '2px', background: item.color, flexShrink: 0 }} />
                    <span style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.category}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: '600', color: '#1e293b', flexShrink: 0 }}>{item.amount}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Modal open={!!viewRow} onClose={() => setViewRow(null)} title={viewRow ? `Expense ${viewRow.ref}` : ""} size="lg">
        {viewRow && (
          <div>
            <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }} className="ess-rg-2">
              <div><label style={labelStyle}>Employee</label><p style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{viewRow.employee}</p></div>
              <div><label style={labelStyle}>Department</label><p style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{viewRow.department}</p></div>
              <div><label style={labelStyle}>Category</label><p style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{viewRow.category}</p></div>
              <div><label style={labelStyle}>Amount</label><p style={{ fontSize: '18px', color: '#1e293b', fontWeight: '700' }}>{viewRow.amount}</p></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Description</label><p style={{ fontSize: '14px', color: '#475569' }}>{viewRow.description}</p></div>
              <div><label style={labelStyle}>Submitted</label><p style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>{viewRow.submittedFull}</p></div>
              <div><label style={labelStyle}>Status</label><span style={{ background: getStatusBg(viewRow.status), color: getStatusColor(viewRow.status), padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600', display: 'inline-block' }}>{viewRow.status}</span></div>
            </div>
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              {canManage && viewRow.state === "submitted" && (
                <>
                  <button onClick={() => handleAction(viewRow.id, "refused")} style={{ padding: '10px 24px', border: '1px solid #e11d48', borderRadius: '8px', background: 'white', color: '#e11d48', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Refuse</button>
                  <button onClick={() => handleAction(viewRow.id, "approved")} style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', background: '#16a34a', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Approve</button>
                </>
              )}
              {canManage && viewRow.state === "approved" && (
                <button onClick={() => handleAction(viewRow.id, "paid")} style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', background: '#0891b2', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Mark as Paid</button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Reports Modal */}
      <Modal open={showReports} onClose={() => setShowReports(false)} title="Expense Reports" size="lg">
        <div>
          <div className="ess-sg ess-sg3" style={{ marginBottom: '24px' }}>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>{data.reduce((s, r) => s + (r.amountRaw || 0), 0).toLocaleString()} FCFA</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Total Spend (All Time)</div>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>{data.length}</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Total Claims</div>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>{data.filter(r => r.status === "Approved" || r.status === "Paid").length}</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Approved / Paid</div>
            </div>
          </div>

          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Monthly Distribution</h4>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', height: '120px', gap: '8px', marginBottom: '24px' }}>
            {monthlyTrend.length === 0 ? (
              <div style={{ width: '100%', textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '13px' }}>No data</div>
            ) : monthlyTrend.map((item) => {
              const h = (item.amount / maxAmount) * 100;
              return (
                <div key={item.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{ width: '100%', height: `${Math.max(h, 4)}px`, background: '#4f46e5', borderRadius: '4px 4px 0 0', minHeight: '4px', transition: 'height 0.5s ease' }} />
                  <div style={{ fontSize: '10px', color: '#64748b', marginTop: '6px', fontWeight: '500' }}>{item.month}</div>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: '#1e293b' }}>{(item.amount/1000).toFixed(0)}k</div>
                </div>
              );
            })}
          </div>

          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Category Breakdown</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {categoryBreakdown.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: '13px' }}>No data</div>
            ) : categoryBreakdown.map((item, i) => (
              <div key={item.category}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                  <span style={{ color: '#475569' }}>{item.category}</span>
                  <span style={{ fontWeight: '600', color: '#1e293b' }}>{item.amount}%</span>
                </div>
                <div style={{ height: 8, background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${item.amount}%`, background: catColors[i % catColors.length], borderRadius: '4px', transition: 'width 1s ease' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* New Expense Modal */}
      <Modal open={showNewForm} onClose={() => setShowNewForm(false)} title="New Expense Claim" size="md">
        <div>
          <FormField label="Employee *">
            <select value={formData.employee_id} onChange={(e) => setFormData(f => ({ ...f, employee_id: e.target.value }))} style={inputStyle}>
              <option value="">Select employee…</option>
              {empOptions.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </FormField>
          <FormField label="Category">
            <select value={formData.category_id} onChange={(e) => setFormData(f => ({ ...f, category_id: e.target.value }))} style={inputStyle}>
              <option value="">Select category…</option>
              <option value="1">Travel</option>
              <option value="2">Accommodation</option>
              <option value="3">Meals</option>
              <option value="4">Equipment</option>
              <option value="5">Training</option>
              <option value="6">Other</option>
            </select>
          </FormField>
          <FormField label="Description">
            <input value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} placeholder="What is this expense for?" style={inputStyle} />
          </FormField>
          <FormField label="Amount (FCFA) *">
            <input type="number" value={formData.amount} onChange={(e) => setFormData(f => ({ ...f, amount: e.target.value }))} placeholder="0" min="0" style={inputStyle} />
          </FormField>
          <FormField label="Date">
            <input type="date" value={formData.date} onChange={(e) => setFormData(f => ({ ...f, date: e.target.value }))} style={inputStyle} />
          </FormField>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
            <button onClick={() => setShowNewForm(false)} style={{ padding: '10px 24px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', color: '#475569', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleCreate} disabled={formLoading} style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', background: formLoading ? '#94a3b8' : '#059669', color: 'white', fontSize: '14px', fontWeight: '600', cursor: formLoading ? 'not-allowed' : 'pointer' }}>
              {formLoading ? "Submitting…" : "Submit Expense"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelExpensesPage;